import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useQuery,
} from '@apollo/client';

import { ErrorHandler, CenteredLoader } from '@playbookux/playbook-web-core';
import ImageComponent from './image-component';

import {
  TEST_SESSION_ANSWERS_BY_TEST_SESSION_ID,
} from '../../../core/graphql/tree-test';

import {
  Container,
  ContainerHeader,
  Dialog,
  Header,
  ContainerTitle,
  ContainerLabel,
  Dropdown,
  ContainerSection,
  Square,
  ContainerSquare,
  LabelIndirect,
  ContainerDropdown,
} from './styles';

const IMAGE_NAME = {
  forward: 'Forward',
  backward: 'Backward',
  sibling: 'Sibling',
};

const RESULT_TYPE = {
  'Direct Success': '#2DA545',
  'Indirect Success': '#A6FFB8',
  'Direct Failure': '#EC2C42',
  'Indirect Failure': '#FF8796',
};

const TreeTestModal = (props) => {
  const {
    onClose,
    show,
    testSessionId,
  } = props;

  const {
    loading,
    error,
    data = {},
  } = useQuery(TEST_SESSION_ANSWERS_BY_TEST_SESSION_ID, {
    variables: {
      testSessionId,
    },
  });

  const { testSessionAnswers = [] } = data;

  const options = useMemo(() => testSessionAnswers.map((tsa) => ({
    key: tsa.id,
    value: tsa,
    text: `Task ${tsa.question.questionOrder + 1}`,
  })),
  [testSessionAnswers]);

  const [selectValue, setSelectValue] = useState(null);
  const {
    question: {
      label,
    } = {},
    treeTestResult,
    treeTestPath,
  } = selectValue || {};

  const transformedPath = useMemo(() => {
    const tokens = (treeTestPath || '')
      .split(/\s(<|>|<->)\s/g)
      .map((token) => {
        switch (token) {
          case '>':
            return <ImageComponent imageName={IMAGE_NAME.forward} />;
          case '<':
            return <ImageComponent imageName={IMAGE_NAME.backward} />;
          case '<->':
            return <ImageComponent imageName={IMAGE_NAME.sibling} />;
          default:
            return token;
        }
      });

    return (
      <>
        Home
        <ImageComponent imageName={IMAGE_NAME.forward} />
        {tokens.map((t) => t)}
      </>
    );
  }, [treeTestPath]);

  const handleChange = (e, { value }) => {
    setSelectValue(value);
  };

  useEffect(() => {
    if (options && options.length) {
      setSelectValue(options[0].value);
    }
  }, [options]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving path answers."
      />
    );
  }

  return (
    <Container
      active={show}
      onClickOutside={onClose}
      page
    >
      <Dialog>
        {loading && (
          <CenteredLoader />
        )}
        <ContainerHeader>

          <ContainerTitle>
            <Header as="h3">Tree Test</Header>
          </ContainerTitle>

          <ContainerLabel>

            <ImageComponent
              imageName={IMAGE_NAME.forward}
              text="Forward"
              marginHeader
            />
            <ImageComponent
              imageName={IMAGE_NAME.backward}
              text="Backward"
              marginHeader
            />
            <ImageComponent
              imageName={IMAGE_NAME.sibling}
              text="Sibling"
              marginHeader
            />

          </ContainerLabel>

        </ContainerHeader>

        <ContainerSection>
          <ContainerDropdown>
            <Dropdown
              onChange={handleChange}
              selection
              options={options}
              value={selectValue}
            />
            {label}
          </ContainerDropdown>
        </ContainerSection>

        <ContainerSection>
          <LabelIndirect>
            <ContainerSquare>
              <Square resultType={RESULT_TYPE[treeTestResult]} />
              {' '}
              {treeTestResult}
            </ContainerSquare>
          </LabelIndirect>
        </ContainerSection>

        <ContainerSection>
          <ContainerLabel>
            {transformedPath}
          </ContainerLabel>
        </ContainerSection>

      </Dialog>
    </Container>
  );
};

TreeTestModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  testSessionId: PropTypes.string.isRequired,
};

TreeTestModal.defaultProps = {
  show: false,
};

export default TreeTestModal;
